'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ProductCard from '../components/ProductCard';
import Image from 'next/image';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import * as Icons from '@fortawesome/free-solid-svg-icons';

type Group = {
  _id: string;
  displayName: string;
  displayImage: string;
  lowestPrice: number;
  tags: string[];
};

type Tag = {
  _id: string;
  name: string;
  color: string;
  icon?: string;
};

type Props = {
  initialProducts: Group[];
  tags: Tag[];
};

export default function ProductsPageClient({ initialProducts, tags }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(4);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const suggestions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return initialProducts
      .filter(g => (g.displayName ?? "").toLowerCase().includes(q))
      .slice(0, 5);
  }, [searchTerm, initialProducts]);

  const filteredProducts = useMemo(() => {
    let filtered = initialProducts;

    // Filter by search term
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(g => 
        (g.displayName ?? "").toLowerCase().includes(q)
      );
    }

    // Filter by selected category
    if (selectedCategory) {
      filtered = filtered.filter(g => 
        g.tags.includes(selectedCategory)
      );
    }

    return filtered;
  }, [initialProducts, searchTerm, selectedCategory]);

  const visibleProducts = useMemo(() => {
    return filteredProducts.slice(0, visibleCount);
  }, [filteredProducts, visibleCount]);

  const hasMore = visibleCount < filteredProducts.length;

  function highlightMatch(text: string, query: string) {
    const q = query.trim();
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className="bg-transparent text-orange-300 underline">{text.slice(i, i + q.length)}</mark>
        {text.slice(i + q.length)}
      </>
    );
  }

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setOpen(false);
        }
      },
      {
        root: null,
        threshold: 1.0,
      }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  const selectCategory = (tagId: string) => {
    setSelectedCategory(tagId);
    setCategoryDropdownOpen(false);
    setVisibleCount(16); // Reset to initial count when filtering
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSearchTerm('');
    setVisibleCount(16);
    setCategoryDropdownOpen(false);
  };

  return (
    <>
      <section className="pt-28 pb-5">
        <div className="container mx-auto flex flex-col items-center px-4">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 text-center text-white">
            Explore Our <span className="font-extrabold bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent inline-block">Products</span>
          </h1>
          <p className="mx-5 sm:mx-5 text-lg max-w-xl text-center text-gray-500 mb-5">
            Browse through our collection of {initialProducts.length} products
          </p>
          <div className="w-full max-w-xl px-4">
            <div ref={wrapperRef} className="relative w-full">
            <div className="flex items-center rounded-full border border-orange-600 px-4 py-2 shadow-xl">
              <svg className="mr-3 text-orange-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width={20} height={20} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
              </svg>

              <input
                type="text"
                placeholder="Search from over 250+ products"
                className="bg-transparent flex-1 outline-none text-white text-base"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setOpen(true); }}
                onFocus={() => suggestions.length > 0 && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 120)}
                aria-autocomplete="list"
                aria-expanded={open}
                aria-controls="suggestions"
              />
            </div>

            {/* Desktop Category Filter Bar */}
            <div className="hidden lg:block mt-4">
              <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-orange-600 scrollbar-track-gray-800">
                <button
                  onClick={() => { setSelectedCategory(''); setVisibleCount(16); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all flex-shrink-0 ${
                    !selectedCategory
                      ? `bg-orange-600 text-white`
                      : 'bg-[#252525] text-white hover:bg-[#2a2a2a]'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  <span className="font-medium">All Categories</span>
                </button>
                {tags.map((tag) => (
                  <button
                    key={tag._id}
                    onClick={() => selectCategory(tag._id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all flex-shrink-0 ${
                      selectedCategory === tag._id
                        ? `bg-orange-600 text-white`
                        : 'bg-[#252525] text-white hover:bg-[#2a2a2a]'
                    }`}
                  >
                    {tag.icon ? (
                      <FontAwesomeIcon icon={(Icons as any)[tag.icon]} className="w-4 h-4" style={{ color: selectedCategory === tag._id ? 'white' : tag.color }} />
                    ) : (
                      <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                    )}
                    <span className="font-medium">{tag.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Category Dropdown */}
            <div className="lg:hidden mt-3 relative">
              <button
                onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-[rgba(249,115,22,0.1)] border border-orange-600/25 rounded-lg text-white"
              >
                <span>{selectedCategory ? tags.find(t => t._id === selectedCategory)?.name || 'Select Category' : 'All Categories'}</span>
                <svg className={`w-5 h-5 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {categoryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[rgba(60,25,5)] border border-orange-600/25 rounded-lg shadow-lg overflow-hidden z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setSelectedCategory(''); setCategoryDropdownOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors border-b border-gray-700 ${
                      !selectedCategory ? 'bg-orange-600 text-white' : 'text-white hover:bg-orange-600/20'
                    }`}
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </div>
                    <span className="font-medium">All Categories</span>
                  </button>
                  {tags.map((tag) => (
                    <button
                      key={tag._id}
                      onClick={() => selectCategory(tag._id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 transition-colors border-b border-gray-700 last:border-b-0 ${
                        selectedCategory === tag._id ? 'bg-orange-600 text-white' : 'text-white hover:bg-orange-600/20'
                      }`}
                    >
                      {tag.icon ? (
                        <FontAwesomeIcon icon={(Icons as any)[tag.icon]} className="w-5 h-5" style={{ color: tag.color }} />
                      ) : (
                        <div className="w-6 h-6 rounded flex-shrink-0" style={{ backgroundColor: tag.color }} />
                      )}
                      <span className="font-medium truncate">{tag.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {open && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 z-50 overflow-hidden rounded-2xl border border-orange-600/40 bg-slate-900/95 backdrop-blur shadow-2xl">
                <ul id="suggestions" role="listbox" className="divide-y divide-white/5">
                  {suggestions.slice(0, 3).map((g, idx) => (
                    <Link key={g._id} href={`/products/${g._id}`}>
                      <li
                        role="option"
                        aria-selected={idx === active}
                        onMouseEnter={() => setActive(idx)}
                        className={`px-4 py-3 flex items-center gap-3 cursor-pointer ${idx === active ? "bg-orange-600/20" : "hover:bg-orange-600/10"}`}
                      >
                        {g.displayImage && (
                          <Image src={g.displayImage} width={420} height={320} alt="" className="h-10 w-20 rounded-md object-cover" priority />
                        )}
                        <div className="min-w-0">
                          <div className="truncate text-white">{highlightMatch(g.displayName, searchTerm)}</div>
                          {typeof g.lowestPrice === "number" && g.lowestPrice > 0 && (
                            <div className="text-xs text-white/70">From ${g.lowestPrice.toFixed(2)}</div>
                          )}
                        </div>
                      </li>
                    </Link>
                  ))}
                </ul>
              </div>
            )}
          </div>
          </div>
        </div>
      </section>

      <section className={`container mx-auto max-w-[1200px] px-2 sm:px-6 py-6 ${open ? "sm:py-30" : "sm:py-12"}`}>
        <div className="flex gap-6 relative">
          {/* Sidebar - Desktop Only */}
          {/*<aside className="hidden lg:block lg:sticky lg:top-6 w-64 bg-[#1a1a1a] border border-orange-600/25 rounded-lg p-4 overflow-y-auto h-fit">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-white">Explore Categories</h2>
            </div>

            <div className="mb-2">
              <button
                onClick={clearFilters}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  !selectedCategory && !searchTerm
                    ? 'bg-orange-600 text-white'
                    : 'bg-[#252525] text-white hover:bg-[#2a2a2a]'
                }`}
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </div>
                <span className="font-medium">All Categories</span>
              </button>
            </div>

            <div>
              {tags.map((tag) => (
                <button
                  key={tag._id}
                  onClick={() => selectCategory(tag._id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 mb-2 rounded-lg transition-colors ${
                    selectedCategory === tag._id
                      ? 'bg-orange-600 text-white'
                      : 'bg-[#252525] text-white hover:bg-[#2a2a2a]'
                  }`}
                >
                  {tag.icon ? (
                    <FontAwesomeIcon icon={(Icons as any)[tag.icon]} className="w-5 h-5" style={{ color: 'white' }} />
                  ) : (
                    <div className="w-6 h-6 rounded flex-shrink-0" style={{ backgroundColor: tag.color }} />
                  )}
                  <span className="font-medium truncate">{tag.name}</span>
                </button>
              ))}
            </div>
          </aside>*/}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="mb-6 flex items-center justify-between px-2">
              <h2 className="text-xl sm:text-2xl font-semibold">
                {selectedCategory || searchTerm ? `Filtered Products` : 'All Products'}
              </h2>
              <span className="text-gray-400">{filteredProducts.length} found</span>
            </div>

            {visibleProducts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 justify-items-center">
                  {visibleProducts.map((group) => (
                    <ProductCard
                      key={group._id}
                      categoryId={group._id}
                      name={group.displayName}
                      price={group.lowestPrice}
                      imageUrl={group.displayImage}
                    />
                  ))}
                </div>

                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={() => setVisibleCount(prev => prev + 3)}
                      className="px-8 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold"
                    >
                      Show More
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg">No products found matching your criteria.</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Reset Search
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
